import { Box, Typography, useTheme } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetHoldingRealizedGainsQuery,
  useGetHoldingUnrealizedGainsQuery,
  RealizedGainsQueryParams,
  UnrealizedGainsQueryParams,
  RealizedGainsRecord,
} from '@/store/holdingsApi';
import { useMemo } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  formatTaxLotNumber,
  getTaxLotNumberSortComparator,
} from '@/modules/portfolio/gainLoss/constants';

export interface TaxLotsDetailGridProps {
  realized: boolean;
  holdingId: number;
  investorId?: number;
  startDate?: string;
  endDate?: string;
  parentColumns: GridColDef[];
}

export const TaxLotsDetailGrid = ({
  realized,
  holdingId,
  investorId,
  startDate,
  endDate,
  parentColumns,
}: TaxLotsDetailGridProps) => {
  const theme = useTheme();
  const authLoaded = useAuthIsLoaded();
  const { t } = useLocalization();

  const isRealized = realized;

  const {
    data: realizedData,
    isFetching: realizedFetching,
    error: realizedError,
  } = useGetHoldingRealizedGainsQuery(
    authLoaded && isRealized && holdingId
      ? ({
          holdingId,
          startDate: startDate || '',
          endDate: endDate || '',
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          investorId,
        } satisfies RealizedGainsQueryParams)
      : skipToken,
  );

  const {
    data: unrealizedData,
    isFetching: unrealizedFetching,
    error: unrealizedError,
  } = useGetHoldingUnrealizedGainsQuery(
    authLoaded && !isRealized && holdingId
      ? ({
          holdingId,
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          investorId,
        } satisfies UnrealizedGainsQueryParams)
      : skipToken,
  );

  const data = isRealized ? realizedData : unrealizedData;
  const isLoading = isRealized ? realizedFetching : unrealizedFetching;
  const error = isRealized ? realizedError : unrealizedError;

  const rows = useMemo(() => {
    if (!data?.Records) return [];

    return data.Records.map((record, index) => {
      return {
        id: `lot-${String(holdingId)}-${String(index)}`,
        TaxLotNumber: formatTaxLotNumber(record.TaxLotNumber),
        HoldingName: record.HoldingName,
        Symbol: record.Symbol || '',
        CUSIP: record.CUSIP || '',
        SaleType:
          'SaleType' in record
            ? (record as RealizedGainsRecord).SaleType || 'Sale'
            : undefined,
        Quantity: record.Quantity,
        OpenDate: record.OpenDate,
        TradeDate:
          'TradeDate' in record
            ? (record as RealizedGainsRecord).TradeDate || record.OpenDate
            : record.OpenDate,
        SaleDate:
          'SaleDate' in record
            ? (record as RealizedGainsRecord).SaleDate || record.OpenDate
            : record.OpenDate,
        CostBasis: record.CostBasis,
        SalePrice:
          'SalePrice' in record
            ? (record as RealizedGainsRecord).SalePrice || record.CurrentBalance
            : undefined,
        Price: record.Price,
        CurrentBalance: record.CurrentBalance,
        DollarChange: record.DollarChange,
        PercentChange: record.PercentChange,
        TaxLotTermDisplay: record.TaxLotTermDisplay,
        account: record.AccountName,
        ClassificationName: 'Unclassified', // Fallback if not provided in tax lot endpoint
        gainOrLoss: record.DollarChange >= 0 ? t('gain') : t('loss'),
      };
    });
  }, [data?.Records, holdingId, t]);

  const columns = useMemo(() => {
    // Return a slightly modified version of parent columns for the detail panel.
    // For example, replace the holding name with the lot number so we can see which lot it is.
    return parentColumns.map((col) => {
      if (col.field === 'HoldingName') {
        return {
          ...col,
          field: 'TaxLotNumber',
          headerName: t('grid.lotNumber'),
          valueFormatter: undefined,
          renderCell: undefined,
          getSortComparator: getTaxLotNumberSortComparator,
        };
      }
      return {
        ...col,
        // Disable sorting on child grid columns if desired, but we can keep it.
        sortable: false,
        filterable: false,
      };
    });
  }, [parentColumns, t]);

  // Defensive: ensure we never pass undefined/null entries into the grid.
  // DataGrid internals expect every column to be a valid object and can crash
  // when encountering invalid entries (e.g. reading `filterable` of undefined).
  const safeColumns: GridColDef[] = useMemo(
    () => columns.filter((c): c is GridColDef => typeof c.field === 'string'),
    [columns],
  );

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>
          {t('errorLoadingData')}: {JSON.stringify(error)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 2, backgroundColor: theme.palette.action.hover, height: '100%' }}
    >
      <DataGridPremium
        rows={rows}
        columns={safeColumns}
        loading={isLoading}
        hideFooter
        autoHeight
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        density='compact'
        sx={{
          backgroundColor: theme.palette.background.paper,
          '& .positive-value': {
            color: theme.palette.success.main,
            fontWeight: 600,
          },
          '& .negative-value': {
            color: theme.palette.error.main,
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};
